import React, { useMemo } from 'react';
import { useT } from '../i18n';

export default function ShippingAddressFields({ value, onChange }) {
  const t = useT();
  // Shared shipping-address inputs used by both checkout and the account-side
  // order edit. Tuned for Israeli addresses where street names/numbers are often
  // missing in villages — neighborhood + landmark help the courier find the home.
  //
  // Each field carries `autoComplete` + `name` so browsers reliably autofill the
  // address (fewer typos, higher completion), and the right `inputMode`/`type`/
  // `pattern` for on-device keyboards and native validation. The postal code
  // (mikud) is required and numeric (no fixed length) — couriers need it.
  const FIELDS = useMemo(() => [
    { key: 'fullName', label: t.checkoutFullName, type: 'text', required: true, autoComplete: 'name' },
    { key: 'phone', label: t.checkoutPhone, type: 'tel', required: true, autoComplete: 'tel', inputMode: 'tel' },
    { key: 'city', label: t.checkoutCity, type: 'text', required: true, autoComplete: 'address-level2' },
    {
      key: 'postalCode', label: t.checkoutPostalCode, type: 'text', required: true,
      autoComplete: 'postal-code', inputMode: 'numeric', pattern: '[0-9]*',
      maxLength: 7, title: t.checkoutPostalCodeInvalid,
    },
    { key: 'neighborhood', label: t.checkoutNeighborhood, type: 'text', autoComplete: 'address-level3', placeholder: t.checkoutNeighborhoodPlaceholder },
    { key: 'address', label: t.checkoutAddress, type: 'text', required: true, autoComplete: 'street-address' },
    { key: 'landmark', label: t.checkoutLandmark, type: 'text', autoComplete: 'off', placeholder: t.checkoutLandmarkPlaceholder },
  ], [t]);
  // Keep the numeric postal code clean (strip anything non-digit) so the stored
  // value always matches the pattern, regardless of paste/keyboard quirks.
  const set = (key, numeric) => (e) => {
    const raw = e.target.value;
    const next = numeric ? raw.replace(/\D/g, '') : raw;
    onChange({ ...value, [key]: next });
  };

  return (
    <>
      {FIELDS.map((f) => (
        <React.Fragment key={f.key}>
          <label className="field-label">
            {f.label}
            {!f.required && <span className="field-optional"> ({t.checkoutOptional})</span>}
          </label>
          <input
            type={f.type}
            name={f.key}
            className="field-input"
            value={value[f.key] || ''}
            onChange={set(f.key, f.inputMode === 'numeric')}
            placeholder={f.placeholder || ''}
            inputMode={f.inputMode}
            pattern={f.pattern}
            maxLength={f.maxLength}
            title={f.title}
            autoComplete={f.autoComplete}
            required={f.required}
          />
        </React.Fragment>
      ))}
      <p className="field-hint">{t.checkoutShippingHint}</p>
    </>
  );
}
